/**
 * Feedback Master — create / edit a feedback DEFINITION.
 *
 * A page of its own (not a modal), with `useNavigationBlocker` +
 * `DiscardChangesDialog`, matching every other master form in the app: a
 * definition takes real work to fill in and must not be lost to a stray click.
 *
 * Every enum picker on this page is fed from `value_sets_master`. Nothing here
 * declares a componentType, a status or a UI mode.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InputSwitch } from 'sharedUi/InputSwitch';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { Dropdown } from 'sharedUi/Dropdown';
import { Dialog } from 'sharedUi/Dialog';
import { Button } from 'sharedUi/Button';
import { Message } from 'sharedUi/Message';
// Straight from PrimeReact, as in notification-master-form: sharedUi has no
// TabView wrapper, and these tabs must look identical across the two screens.
import { TabView, TabPanel } from 'primereact/tabview';
import FormField from '../../components/common/form-field';
import NumberStepper from '../../components/common/number-stepper';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import ScreenHeader from '../../components/common/screen-header';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { useToastApi } from '../../hooks/use-toast-api';
import { feedbackMasterService } from '../../services/feedback-master.service';
import { projectsService } from '../../services/projects.service';
import { api } from '../../services/api';
import type {
  FeedbackContextField, FeedbackMasterBody, FeedbackQuestion, FeedbackSmeRef,
} from '../../types/feedback-master.types';
import QuestionBuilder from './question-builder';
import {
  useAiComponentTypes, useComponentContextKeys, useContextDataTypes, useSmeRoles,
  useComponentTypes,
} from './use-feedback-value-sets';
import { isAiComponentType, optionsForScope, type FeedbackScope } from '../feedback/feedback-scope';

/* Where the definition lists actually live. `/feedback-master` is a redirect to
   the CORE module, so returning there after saving an AI form dropped the author
   into the wrong screen — and out of the tab they were working in. */
const CORE_LIST = '/feedback?tab=definitions';
const AI_LIST = '/ai-feedback?tab=definitions';

/* Role is not a label: it decides who is actually asked to act on feedback. */
const SME_ROLE_INFO =
  'Watcher (the default) = notified and can read it, but is never asked to act and never blocks. Reviewer = gets a work item for every submission and must respond; their verdict '
  + 'can close the case. The Primary reviewer is the one whose verdict decides the '
  + 'outcome, so the primary cannot be a Watcher.';

const COMPONENT_CODE_INFO =
  'WHICH specific component this form is for. Leave blank to cover every component '
  + 'of this type — that is the project fallback. It must match exactly what the '
  + 'widget sends, so check it with a developer.';

const SCREEN_CODE_INFO =
  'Leave blank for every screen — that is the project default. Or name one screen '
  + 'to override it just there. It is the screen code, not the URL: errormaster, '
  + 'units, valuesets. See the setup guide for the full list.';

/* Context capture is the one part of this form that is not self-evident, so the
   two fields that decide whether it works at all explain themselves on hover. */
const CTX_KEY_INFO =
  'The NAME this value is stored under on the feedback record. Your own label — pick something readable, like userQuestion. Reports and exports show this name.';
const SOURCE_PATH_INFO =
  'WHERE the widget finds that value in the page data, written as a dotted path — '
  + 'e.g. chat.question reads the "question" property inside "chat". If the path is '
  + 'wrong nothing is captured, so check it with a developer.';

const EMPTY: FeedbackMasterBody = {
  key: '',
  name: '',
  description: '',
  appId: '',
  module: '',
  component: { componentType: '', componentCode: '', componentIdHint: '' },
  questions: [],
  contextCapture: [],
  smeList: [],
  area: '',
  notificationConfig: { notifyOnSubmission: true, notificationCode: '', notifyOnActionRequired: false },
  retention: { retentionDays: null, anonymizeAfterDays: null },
  versionNote: '',
  status: 'ACTIVE',
};

/**
 * The key is free text. It only has to be non-empty and unique within the
 * project — no pattern, no case rule, no required prefix. FDBK.AI.PROMPT.
 * RESPONSE_QUALITY is a convention worth following, not something enforced, so
 * it lives in the placeholder rather than in a validator.
 */

const FeedbackMasterForm = () => {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  /* Which half of the component types this form may choose from. The AI screen
     opens it with ?scope=AI and the Feedback module with ?scope=CORE, so a
     definition is authored in the screen that will list it — a form offering
     every type could file a definition into a screen its author never opens.
     An EDIT is unscoped: the type the definition already has decides where it
     lives, and blanking it on save would move the row out of both screens. */
  const [searchParams] = useSearchParams();
  const requestedScope = (searchParams.get('scope') || '').toUpperCase();
  const formScope: FeedbackScope | undefined =
    requestedScope === 'AI' ? 'AI' : requestedScope === 'CORE' ? 'CORE' : undefined;
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();

  const [form, setForm] = useState<FeedbackMasterBody>(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  /* Two steps, not one long scroll. Everything that describes the FORM lives on
     step 1; who owns the replies and how they are told lives on step 2. Splitting
     there is not arbitrary — step 1 is authored by whoever designs the question,
     step 2 by whoever runs the team that answers it. */
  const [step, setStep] = useState<1 | 2>(1);
  /* Saving an EDIT asks whether the change is a new version of the definition.
     It is a real decision, not a checkbox to overlook: the version is what a
     stored answer is filed under, so bumping it starts a fresh analytics series
     and leaving it keeps this edit inside the current one. */
  const [askVersion, setAskVersion] = useState(false);
  const [newVersion, setNewVersion] = useState(false);
  const [versionNote, setVersionNote] = useState('');
  const [versionTouched, setVersionTouched] = useState(false);
  const initialRef = useRef<string>(JSON.stringify(EMPTY));
  const savedRef = useRef(false);

  const { options: allComponentTypeOptions, seeded: componentTypesSeeded } = useComponentTypes();
  const { options: dataTypeOptions } = useContextDataTypes();
  const { options: smeRoleOptions } = useSmeRoles();

  // What differs per component type. Both are null when the type has no rows,
  // which the renderers read as "no opinion" rather than "show nothing".
  const componentType = form.component.componentType;
  const { values: suggestedContextKeys } = useComponentContextKeys(componentType);

  const aiComponentTypes = useAiComponentTypes();

  /* Back / Cancel / after-save all return here. A CREATE knows the screen it was
     opened from (?scope); an EDIT carries none by design, so the row's own
     component type decides which of the two screens lists it. */
  const listRoute = useMemo(() => {
    if (formScope) return formScope === 'AI' ? AI_LIST : CORE_LIST;
    return isAiComponentType(componentType, aiComponentTypes) ? AI_LIST : CORE_LIST;
  }, [formScope, componentType, aiComponentTypes]);

  const componentTypeOptions = useMemo(() => {
    const scoped = optionsForScope(
      allComponentTypeOptions, isEditMode ? undefined : formScope, aiComponentTypes);
    // A type the row already carries stays selectable whatever the scope says.
    return componentType && !scoped.some((o) => o.value === componentType)
      ? [...scoped, ...allComponentTypeOptions.filter((o) => o.value === componentType)]
      : scoped;
  }, [allComponentTypeOptions, formScope, isEditMode, componentType, aiComponentTypes]);

  /**
   * Owning project. `GET /apps/accessible` is already scope-restricted server-side
   * (platform admin = all, customer admin = their partner's, everyone else = their
   * own), so this dropdown can only ever offer projects the author may write to —
   * and the create route re-checks the choice against the same scope.
   *
   * Picking one is MANDATORY: a definition always belongs to a project. Global
   * defaults (appId = null) stay seed-only, authored by feedback_bootstrap.py.
   */
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 10 * 60 * 1000,
  });
  const projectOptions = useMemo(
    () => accessibleProjects.map((p) => ({
      value: p._id,
      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
    })),
    [accessibleProjects],
  );
  // One project to choose from is not a choice — pick it and lock the field.
  const projectLocked = accessibleProjects.length <= 1;

  useEffect(() => {
    if (isEditMode || form.appId || !accessibleProjects.length) return;
    const first = accessibleProjects[0]._id;
    // The auto-pick is a default, not an edit: fold it into the baseline too, or a
    // pristine create screen would trip the unsaved-changes guard on the way out.
    initialRef.current = JSON.stringify({ ...JSON.parse(initialRef.current), appId: first });
    setForm((f) => ({ ...f, appId: first }));
  }, [isEditMode, form.appId, accessibleProjects]);

  /* SME picker source. Deliberately NOT the audit list (/names-minimal): that one
     is unscoped so it can resolve any id it meets, which as a picker would offer
     users from other projects. Feedback assigned to them routes into an inbox they
     cannot open, and nothing reports it. /assignable applies the caller's project
     scope; a platform admin still sees everyone, which is what lets a global
     definition name anyone. Re-queried per project — changing the project changes
     who can be reached. */
  const usersQ = useQuery({
    queryKey: ['users-assignable', form.appId],
    queryFn: async () => {
      const res = await api.get<{ data: { _id: string; name: string }[] }>(
        '/api/v1/users/assignable', { params: form.appId ? { appId: form.appId } : undefined });
      return res.data.data || [];
    },
    staleTime: 10 * 60 * 1000,
  });
  const userOptions = useMemo(
    () => (usersQ.data ?? []).map((u) => ({ label: u.name, value: u._id })),
    [usersQ.data],
  );

  const defQ = useQuery({
    queryKey: ['feedback-master', id],
    queryFn: () => feedbackMasterService.get(id!),
    enabled: isEditMode,
  });

  useEffect(() => {
    const d = defQ.data;
    if (!d) return;
    const loaded: FeedbackMasterBody = {
      key: d.key,
      name: d.name,
      description: d.description ?? '',
      appId: d.appId ?? '',
      module: d.module ?? '',
      component: {
        componentType: d.component?.componentType ?? '',
        componentCode: d.component?.componentCode ?? '',
        componentIdHint: d.component?.componentIdHint ?? '',
      },
      questions: d.questions ?? [],
      contextCapture: d.contextCapture ?? [],
      smeList: d.smeList ?? [],
      area: d.area ?? '',
      notificationConfig: d.notificationConfig ?? EMPTY.notificationConfig,
      retention: d.retention ?? EMPTY.retention,
      versionNote: d.versionNote ?? '',
      status: d.status,
      isActive: d.isActive,
    };
    setForm(loaded);
    initialRef.current = JSON.stringify(loaded);
  }, [defQ.data]);

  const isDirty = !savedRef.current && JSON.stringify(form) !== initialRef.current;
  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } =
    useNavigationBlocker({ when: isDirty });

  const saveM = useMutation({
    mutationFn: (body: FeedbackMasterBody) => (isEditMode
      ? feedbackMasterService.update(id!, body)
      : feedbackMasterService.create(body)),
    onSuccess: (saved) => {
      ok(isEditMode
        ? `Definition updated${newVersion ? ` — now v${saved.definitionVersion}` : ''}`
        : 'Definition created');
      setAskVersion(false);
      savedRef.current = true;
      qc.invalidateQueries({ queryKey: ['feedback-masters'] });
      qc.invalidateQueries({ queryKey: ['feedback-master', id] });
      handleNavigate(listRoute);
    },
    onError: (e) => fail(e),
  });

  const set = (p: Partial<FeedbackMasterBody>) => setForm((f) => ({ ...f, ...p }));

  // ------------------------------------------------------------- validation
  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    // Non-empty is the only rule: the key is free text.
    if (!form.key.trim()) e.key = 'A key is required.';
    if (!form.name.trim()) e.name = 'A name is required.';
    // Only on create: the owning project is immutable afterwards, and the seeded
    // global defaults legitimately have none.
    if (!isEditMode && !form.appId) e.appId = 'Select the project this definition belongs to.';
    if (!form.component.componentType) e.componentType = 'Pick which component this definition is about.';
    if (!form.questions.length) e.questions = 'At least one question is required.';

    const codes = form.questions.map((q) => q.questionCode).filter(Boolean);
    if (new Set(codes).size !== codes.length) e.questions = 'Question codes must be unique.';
    if (form.questions.some((q) => !q.questionCode.trim() || !q.label.trim() || !q.questionType)) {
      e.questions = e.questions || 'Every question needs a code, a label and a type.';
    }
    for (const role of ['SENTIMENT', 'RATING']) {
      if (form.questions.filter((q) => (q.signalRole || '').toUpperCase() === role).length > 1) {
        e.questions = `Only one question may carry the ${role} signal role.`;
      }
    }
    const ctxKeys = (form.contextCapture || []).map((c) => c.contextKey).filter(Boolean);
    if (new Set(ctxKeys).size !== ctxKeys.length) e.contextCapture = 'Context keys must be unique.';
    if ((form.contextCapture || []).some((c) => !c.contextKey.trim() || !c.sourcePath.trim() || !c.dataType)) {
      e.contextCapture = e.contextCapture || 'Every context field needs a key, a source path and a type.';
    }
    const smeRows = form.smeList || [];
    if (smeRows.filter((s) => s.isPrimary).length > 1) e.smeList = 'Only one SME may be primary.';
    if (smeRows.some((s) => !s.userId)) e.smeList = e.smeList || 'Every SME row needs a user.';
    if (smeRows.some((s) => !s.role)) e.smeList = e.smeList || 'Every SME needs a role.';
    // The primary's verdict settles the case, and a watcher cannot record one.
    const primary = smeRows.find((s) => s.isPrimary);
    if (primary && (primary.role || '').toUpperCase() === 'WATCHER') {
      e.smeList = 'The primary SME cannot be a Watcher — a watcher cannot record a verdict.';
    }


    return e;
  }, [form, isEditMode]);

  /** Everything a save sends, once the version question (if any) is answered. */
  const save = () => {
    // Blank strings mean "not set" to this API, not "empty value".
    const clean = <T,>(v: T): T | null => (typeof v === 'string' && !v.trim() ? null : v) as T | null;
    saveM.mutate({
      ...form,
      // The owning project is fixed at creation; update() drops it either way.
      appId: isEditMode ? undefined : form.appId,
      // Only an edit carries a version decision; a create is always v1.
      createNewVersion: isEditMode ? newVersion : undefined,
      description: clean(form.description),
      module: clean(form.module),
      area: clean(form.area),
      versionNote: isEditMode && newVersion ? versionNote.trim() : clean(form.versionNote),
      component: {
        componentType: form.component.componentType,
        componentCode: clean(form.component.componentCode),
        componentIdHint: clean(form.component.componentIdHint),
      },
    } as FeedbackMasterBody);
  };

  /** Step 2 owns only the SME list; everything else is decided on step 1. */
  const STEP2_ERROR_KEYS = ['smeList'];
  const stepErrors = (n: 1 | 2) => Object.fromEntries(
    Object.entries(errors).filter(([k]) => (n === 2
      ? STEP2_ERROR_KEYS.includes(k)
      : !STEP2_ERROR_KEYS.includes(k))),
  );
  /** Errors worth showing right now: this step's, plus everything once submitted. */
  const visibleErrors = submitted ? errors : stepErrors(step);

  const goNext = () => {
    setSubmitted(true);
    if (Object.keys(stepErrors(1)).length) return;   // fix step 1 before leaving it
    setSubmitted(false);                             // step 2 starts clean
    setStep(2);
    window.scrollTo({ top: 0 });
  };

  const goBack = () => { setStep(1); window.scrollTo({ top: 0 }); };

  const submit = () => {
    setSubmitted(true);
    // A step-1 problem is invisible from here, so go back and show it.
    if (Object.keys(stepErrors(1)).length) { setStep(1); window.scrollTo({ top: 0 }); return; }
    if (Object.keys(errors).length) return;
    if (!isEditMode) { save(); return; }
    // An edit stops here and asks the version question first.
    setNewVersion(false);
    setVersionNote('');
    setVersionTouched(false);
    setAskVersion(true);
  };

  /** A new version has to say why; that is the whole point of the note. */
  const versionBlocked = newVersion && !versionNote.trim();

  // ----------------------------------------------------------------- render
  const ctx = form.contextCapture || [];
  const smes = form.smeList || [];
  const patchCtx = (i: number, p: Partial<FeedbackContextField>) =>
    set({ contextCapture: ctx.map((c, n) => (n === i ? { ...c, ...p } : c)) });
  const patchSme = (i: number, p: Partial<FeedbackSmeRef>) =>
    set({ smeList: smes.map((s, n) => (n === i ? { ...s, ...p } : s)) });

  return (
    <>
      {toast}
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen"
        data-testid="SCR-FeedbackMaster-Form">
        <ScreenHeader
          icon="pi pi-comment"
          title={isEditMode ? `Edit ${form.key || 'definition'}` : 'New feedback definition'}
          subtitle={isEditMode
            ? `Version ${defQ.data?.definitionVersion ?? 1} · changing the question shape bumps it`
            : 'What to ask, about what, captured with which context, owned by whom'}
          onBack={() => handleNavigate(listRoute)}
        />

        {!componentTypesSeeded && (
          <Message severity="warn" className="mb-3 w-full justify-start"
            text="Feedback component options aren't available yet. Contact your administrator — the pickers below stay empty until they're configured." />
        )}

        <div className="rounded border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <TabView className="feedback-master-tabs"
          activeIndex={step - 1}
          onTabChange={(e: { index: number }) => setStep((e.index + 1) as 1 | 2)}>
        <TabPanel header="Definition">
        {/* ---------------------------------------------------------- identity */}
        <section className="mb-3 rounded border border-gray-200 p-4 dark:border-gray-700">
          <h3 className="mb-3 text-sm font-semibold text-ink">Identity</h3>
          <div className="grid grid-cols-12 items-start gap-x-4 gap-y-4">
            <FormField className="col-span-12 sm:col-span-5" label="Key" required
              error={submitted ? errors.key : undefined}>
              <Input name="key" value={form.key} disabled={isEditMode} className="w-full"
                placeholder="FDBK.CHATBOT.ANSWER_QUALITY"
                onChange={(v: string) => set({ key: v })} />
            </FormField>

            <FormField className="col-span-12 sm:col-span-4" label="Name" required
              error={submitted ? errors.name : undefined}>
              <Input name="name" value={form.name} className="w-full"
                placeholder="Chatbot answer quality"
                onChange={(v: string) => set({ name: v })} />
            </FormField>

            <FormField className="col-span-12 sm:col-span-3" label="Project" required
              error={submitted ? errors.appId : undefined}>
              {isEditMode && !form.appId ? (
                // A seeded global default has no project. Do not present an empty
                // required picker for it: the field is immutable and never sent.
                <Input name="appId" value="Global default (all projects)" disabled className="w-full"
                  onChange={() => undefined} />
              ) : (
                <Dropdown value={form.appId || ''} options={projectOptions}
                  optionLabel="label" optionValue="value" className="w-full"
                  placeholder="Select a project"
                  disabled={isEditMode || projectLocked}
                  onChange={(e: { value: string }) => set({ appId: e.value })} />
              )}
            </FormField>

            <FormField className="col-span-12 sm:col-span-6" label="Module">
              <Input name="module" value={form.module || ''} className="w-full" placeholder="CHATBOT"
                onChange={(v: string) => set({ module: v.toUpperCase() })} />
            </FormField>
            <FormField className="col-span-12 sm:col-span-6" label="Area">
              <Input name="area" value={form.area || ''} className="w-full" placeholder="Customer support"
                onChange={(v: string) => set({ area: v })} />
            </FormField>

            <FormField className="col-span-12" label="Description">
              <TextArea rows={2} value={form.description || ''} className="w-full text-sm"
                placeholder="Asks the user whether the chatbot's answer actually helped."
                onChange={(v: string) => set({ description: v })} />
            </FormField>
          </div>
        </section>

        {/* --------------------------------------------------------- component */}
        <section className="mb-3 rounded border border-gray-200 p-4 dark:border-gray-700">
          <h3 className="mb-3 text-sm font-semibold text-ink">What is being reviewed</h3>
          <div className="grid grid-cols-12 items-start gap-x-4 gap-y-4">
            <FormField className="col-span-12 sm:col-span-4" label="Component type" required
              error={submitted ? errors.componentType : undefined}>
              <Dropdown value={form.component.componentType} options={componentTypeOptions}
                optionLabel="label" optionValue="value" className="w-full" filter resetFilterOnHide
                placeholder="Select a component type"
                onChange={(e: { value: string }) => set({ component: { ...form.component, componentType: e.value } })} />
            </FormField>
            {/* Free text for every component type. It must match EXACTLY what the
                widget sends — for a screen that is the app's own screen id, not
                the URL — so the codes are listed on hover, and a value that
                matches nothing is called out below rather than saving silently
                into a definition that can never resolve. */}
            <FormField className="col-span-12 sm:col-span-4" label="Component code"
              info={form.component.componentType === 'SCREEN' ? SCREEN_CODE_INFO : COMPONENT_CODE_INFO}>
              <Input name="componentCode" value={form.component.componentCode || ''} className="w-full"
                placeholder={form.component.componentType === 'SCREEN'
                  ? 'errormaster'
                  : 'PROMPT.PETITION.SUMMARY'}
                onChange={(v: string) => set({ component: { ...form.component, componentCode: v.trim() } })} />
            </FormField>
            <FormField className="col-span-12 sm:col-span-4" label="Component id hint">
              <Input name="componentIdHint" value={form.component.componentIdHint || ''} className="w-full"
                placeholder="location.pathname"
                onChange={(v: string) => set({ component: { ...form.component, componentIdHint: v } })} />
            </FormField>
          </div>
        </section>

        {/* --------------------------------------------------------- questions */}
        <section className="mb-3 rounded border border-gray-200 p-4 dark:border-gray-700">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink">
              Questions <span className="text-gray-400">· what the user is asked</span>
            </h3>
            {submitted && errors.questions && (
              <span className="text-xs text-red-500">{errors.questions}</span>
            )}
          </div>
          <QuestionBuilder value={form.questions} submitted={submitted}
            onChange={(questions: FeedbackQuestion[]) => set({ questions })} />
        </section>

        {/* ---------------------------------------------------- context capture */}
        <section className="mb-3 rounded border border-gray-200 p-4 dark:border-gray-700">
          <div className="mb-1 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink">
              Context capture <span className="text-gray-400">· a server-side allowlist</span>
            </h3>
            <Button label="Add field" icon="pi pi-plus" text
              onClick={() => set({ contextCapture: [...ctx, { contextKey: '', sourcePath: '', dataType: '', required: false, isSensitive: false }] })} />
          </div>
          <p className="mb-2 text-xs text-ink-muted">
            Anything the client sends that is not declared here is dropped, so this list is
            both the documentation and the enforcement.
          </p>

          {/* The context worth capturing differs per component type — a SCREEN has a
              route and a load time, a PROMPT has the user's words and the model's
              answer. The suggestions come from a value set, so they are editable. */}
          {!!suggestedContextKeys?.length && (
            <div className="mb-3 rounded-md border border-dashed border-indigo-200 bg-indigo-50/40 p-2 dark:border-indigo-800 dark:bg-indigo-900/10">
              <div className="mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                Typical for a {componentType} component — click to add:
              </div>
              <div className="flex flex-wrap gap-1.5">
                {suggestedContextKeys.map((k) => {
                  const already = ctx.some((c) => c.contextKey === k);
                  return (
                    <button
                      key={k}
                      type="button"
                      disabled={already}
                      title={already ? 'Already added' : `Add ${k}`}
                      onClick={() => set({
                        contextCapture: [...ctx, {
                          contextKey: k,
                          // A sensible starting path; the author refines it.
                          sourcePath: k,
                          // Must be a REAL code, not '': a row with a blank dataType
                          // fails validation, which would block Save from a section
                          // the author cannot see. First value-set code, not a
                          // literal, so this stays data-driven.
                          dataType: dataTypeOptions[0]?.value ?? '',
                          required: false,
                          isSensitive: false,
                        }],
                      })}
                      className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${already
                        ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800'
                        : 'border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-100 dark:border-indigo-700 dark:bg-gray-800 dark:text-indigo-300'}`}
                    >
                      {already ? '✓ ' : '+ '}{k}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {submitted && errors.contextCapture && (
            <div className="mb-2 text-xs text-red-500">{errors.contextCapture}</div>
          )}
          {ctx.map((c, i) => (
            <div key={i} className="mb-2 grid grid-cols-12 items-start gap-x-3 gap-y-2">
              <FormField className="col-span-6 sm:col-span-3" label={i === 0 ? 'Context key' : ''}
                info={i === 0 ? CTX_KEY_INFO : undefined}>
                <Input name={`ck-${i}`} value={c.contextKey} className="w-full" placeholder="userQuestion"
                  onChange={(v: string) => patchCtx(i, { contextKey: v })} />
              </FormField>
              <FormField className="col-span-6 sm:col-span-4" label={i === 0 ? 'Source path' : ''}
                info={i === 0 ? SOURCE_PATH_INFO : undefined}>
                <Input name={`cs-${i}`} value={c.sourcePath} className="w-full" placeholder="chat.question"
                  onChange={(v: string) => patchCtx(i, { sourcePath: v })} />
              </FormField>
              <FormField className="col-span-4 sm:col-span-2" label={i === 0 ? 'Type' : ''}>
                <Dropdown value={c.dataType} options={dataTypeOptions}
                  optionLabel="label" optionValue="value" className="w-full" placeholder="Type"
                  onChange={(e: { value: string }) => patchCtx(i, { dataType: e.value })} />
              </FormField>
              <FormField className="col-span-3 sm:col-span-1" label={i === 0 ? 'Req.' : ''}>
                <div className="flex h-[38px] items-center">
                  <InputSwitch checked={!!c.required} onChange={(e) => patchCtx(i, { required: !!e.value })} />
                </div>
              </FormField>
              <FormField className="col-span-4 sm:col-span-1" label={i === 0 ? 'Sensitive' : ''}>
                <div className="flex h-[38px] items-center">
                  <InputSwitch checked={!!c.isSensitive} onChange={(e) => patchCtx(i, { isSensitive: !!e.value })} />
                </div>
              </FormField>
              <div className="col-span-1 flex h-[38px] items-center pt-[22px] sm:pt-0">
                <Button aria-label="Delete" icon="pi pi-trash" text rounded severity="danger"
                  onClick={() => set({ contextCapture: ctx.filter((_, n) => n !== i) })} />
              </div>
            </div>
          ))}
          {!ctx.length && (
            <div className="rounded-md border border-dashed border-gray-300 p-3 text-center text-xs text-gray-500 dark:border-gray-600">
              No context declared — answers will arrive with no idea what they were about.
            </div>
          )}
        </section>

        </TabPanel>

        <TabPanel header="SMEs &amp; Notifications">
        {/* ---------------------------------------------------------- ownership */}
        <section className="mb-3 rounded border border-gray-200 p-4 dark:border-gray-700">
          <div className="mb-1 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink">
              SMEs <span className="text-gray-400">· who owns the reply</span>
            </h3>
            <Button label="Add SME" icon="pi pi-plus" text
              onClick={() => set({
                smeList: [...smes, {
                  // NOT auto-primary any more: a new SME defaults to Watcher, and a
                  // watcher cannot be primary. Whoever decides is marked deliberately.
                  userId: '', isPrimary: false, notificationEnabled: true,
                  // First code in the value set is the default — Watcher today. Kept
                  // data-driven so changing the default is a value-set reorder.
                  role: smeRoleOptions[0]?.value ?? '',
                }],
              })} />
          </div>
          {submitted && errors.smeList && <div className="mb-2 text-xs text-red-500">{errors.smeList}</div>}
          {/* Not an error — a watcher-only list is a legitimate "collect it, nobody is
              on duty yet" state. But it should never be a surprise. */}
          {!!smes.length && !smes.some((s) => (s.role || '').toUpperCase() === 'REVIEWER') && (
            <div className="mb-2 text-xs text-amber-600 dark:text-amber-400">
              No Reviewer on this list — everyone here is only notified, so nobody is
              asked to act on the feedback.
            </div>
          )}
          {/* Changing the project changes who can be reached. Without this the stale
              rows just render blank and the save fails with a server error. */}
          {!usersQ.isFetching && smes.some(
            (s) => s.userId && !userOptions.some((o) => o.value === s.userId)) && (
            <div className="mb-2 text-xs text-red-500">
              Some SMEs above are not in the selected project, so feedback would reach
              nobody. Pick replacements from this project before saving.
            </div>
          )}
          {smes.map((s, i) => (
            <div key={i} className="mb-2 grid grid-cols-12 items-start gap-x-3 gap-y-2">
              <FormField className="col-span-12 sm:col-span-5" label={i === 0 ? 'User' : ''}>
                <Dropdown value={s.userId} options={userOptions} optionLabel="label" optionValue="value"
                  className="w-full" filter resetFilterOnHide placeholder="Select a user"
                  onChange={(e: { value: string }) => patchSme(i, { userId: e.value })} />
              </FormField>
              <FormField className="col-span-6 sm:col-span-3" label={i === 0 ? 'Role' : ''}
                required={i === 0} info={i === 0 ? SME_ROLE_INFO : undefined}>
                <Dropdown value={s.role || ''} options={smeRoleOptions}
                  optionLabel="label" optionValue="value" className="w-full"
                  placeholder="Select a role"
                  onChange={(e: { value: string }) => patchSme(i, { role: e.value })} />
              </FormField>
              <FormField className="col-span-3 sm:col-span-1" label={i === 0 ? 'Primary' : ''}>
                <div className="flex h-[38px] items-center">
                  <InputSwitch checked={!!s.isPrimary}
                    onChange={(e) => set({
                      // Only one primary: setting this one clears the rest.
                      smeList: smes.map((x, n) => ({ ...x, isPrimary: n === i ? !!e.value : false })),
                    })} />
                </div>
              </FormField>
              <FormField className="col-span-3 sm:col-span-2" label={i === 0 ? 'Notify' : ''}>
                <div className="flex h-[38px] items-center">
                  <InputSwitch checked={s.notificationEnabled !== false}
                    onChange={(e) => patchSme(i, { notificationEnabled: !!e.value })} />
                </div>
              </FormField>
              <div className="col-span-1 flex h-[38px] items-center">
                <Button aria-label="Delete" icon="pi pi-trash" text rounded severity="danger"
                  onClick={() => set({ smeList: smes.filter((_, n) => n !== i) })} />
              </div>
            </div>
          ))}
          {!smes.length && (
            <div className="rounded-md border border-dashed border-gray-300 p-3 text-center text-xs text-gray-500 dark:border-gray-600">
              No SME — feedback is still captured, but it reaches nobody's inbox.
            </div>
          )}
        </section>

        {/* ------------------------------------------ notification + retention */}
        <section className="mb-3 rounded border border-gray-200 p-4 dark:border-gray-700">
          <h3 className="mb-3 text-sm font-semibold text-ink">Notification &amp; retention</h3>
          <div className="grid grid-cols-12 items-start gap-x-4 gap-y-4">
            <FormField className="col-span-6 sm:col-span-2" label="Notify on submit">
              <div className="flex h-[38px] items-center">
                <InputSwitch checked={form.notificationConfig?.notifyOnSubmission !== false}
                  onChange={(e) => set({ notificationConfig: { ...form.notificationConfig, notifyOnSubmission: !!e.value } })} />
              </div>
            </FormField>
            <FormField className="col-span-6 sm:col-span-2" label="On action req.">
              <div className="flex h-[38px] items-center">
                <InputSwitch checked={!!form.notificationConfig?.notifyOnActionRequired}
                  onChange={(e) => set({ notificationConfig: { ...form.notificationConfig, notifyOnActionRequired: !!e.value } })} />
              </div>
            </FormField>
            <FormField className="col-span-12 sm:col-span-4" label="Notification code">
              <Input name="notificationCode" value={form.notificationConfig?.notificationCode || ''} className="w-full"
                placeholder="NOTIF.FEEDBACK.SUBMITTED"
                onChange={(v: string) => set({ notificationConfig: { ...form.notificationConfig, notificationCode: v } })} />
            </FormField>
            <FormField className="col-span-6 sm:col-span-2" label="Keep (days)">
              <NumberStepper value={form.retention?.retentionDays ?? null} min={0} max={3650} step={30}
                placeholder="default" ariaLabel="Retention days"
                onChange={(v) => set({ retention: { ...form.retention, retentionDays: v } })} />
            </FormField>
          </div>
        </section>

        </TabPanel>
        </TabView>
        </div>

        {/* A blocked save must SAY so here, beside the button. The field-level
            errors live in sections that are often scrolled off screen, so without
            this the button simply appears to do nothing when the form is invalid. */}
        {submitted && Object.keys(visibleErrors).length > 0 && (
          <Message severity="error" className="mb-3 w-full justify-start"
            text={`Cannot continue — ${Object.values(visibleErrors).join(' · ')}`} />
        )}

        <div className="mt-3 flex justify-end gap-2 pb-6">
          {step === 2 && (
            <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined
              onClick={goBack} />
          )}
          <Button label="Cancel" severity="secondary" outlined onClick={() => handleNavigate(listRoute)} />
          {step === 1 ? (
            <Button label="Next" icon="pi pi-arrow-right" iconPos="right" onClick={goNext} />
          ) : (
            <Button label={isEditMode ? 'Save' : 'Create'} icon="pi pi-check"
              onClick={submit} disabled={saveM.isPending || (isEditMode && defQ.isLoading)} />
          )}
        </div>
      </div>

      <Dialog
        visible={askVersion}
        onHide={() => setAskVersion(false)}
        header="Save changes"
        style={{ width: 520, maxWidth: '95vw' }}
        modal
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Cancel" severity="secondary" outlined
              onClick={() => setAskVersion(false)} disabled={saveM.isPending} />
            <Button label="Save" icon="pi pi-check"
              onClick={() => { setVersionTouched(true); if (!versionBlocked) save(); }}
              disabled={saveM.isPending} />
          </div>
        }
        data-testid="FeedbackMasterForm.Dialog.Version"
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Do you want to save this as a new version of <b>{form.name || form.key}</b>?
          </p>
          <div className="flex items-center gap-3">
            <InputSwitch checked={newVersion} onChange={(e) => setNewVersion(!!e.value)} />
            <span className="text-sm text-ink">
              {newVersion ? 'Yes' : 'No'}
            </span>
          </div>
          {newVersion && (
            <FormField label="Version note" required
              error={versionTouched && !versionNote.trim() ? 'A note is required for a new version.' : undefined}>
              <TextArea rows={3} value={versionNote} className="w-full text-sm"
                onChange={(v: string) => setVersionNote(v)} />
            </FormField>
          )}
        </div>
      </Dialog>

      <DiscardChangesDialog visible={showLeaveDialog} onStay={cancelLeave} onLeave={confirmLeave}
        testId="FeedbackMasterForm.Dialog.Leave" />
    </>
  );
};

export default FeedbackMasterForm;
